import { AxiosResponse } from "axios";
import useSWR, { KeyedMutator } from "swr";
import { Tag } from "@/models/tag";
import ApiService, { defaultSWROption } from "@/services/api/api.service";
import TagEndpoints from "@/services/tag/tag.endpoint";
import { CreateTagRequest, TagSchema, UpdateTagRequest } from "./tag.api.types";

export class TagService {
  static getTagsByWorkspaceId(workspaceId: string): {
    data: Tag[] | undefined;
    error: unknown;
    isLoading: boolean;
    mutate: KeyedMutator<AxiosResponse<TagSchema[], undefined>>;
  } {
    const url = TagEndpoints.GET_TAGS_BY_WORKSPACE(workspaceId);

    const fetcher = () =>
      ApiService.fetchData<undefined, TagSchema[]>({
        url,
        method: "GET",
      });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading, mutate } = useSWR(
      workspaceId ? url : null,
      fetcher,
      defaultSWROption,
    );

    return {
      data: data?.data.map(buildTag),
      error,
      isLoading,
      mutate,
    };
  }

  static async createTag(request: CreateTagRequest): Promise<Tag> {
    const response = await ApiService.fetchData<CreateTagRequest, TagSchema>({
      url: TagEndpoints.CREATE_TAG,
      method: "POST",
      data: request,
    });
    return buildTag(response.data);
  }

  static async updateTag(tagId: string, request: UpdateTagRequest): Promise<Tag> {
    const response = await ApiService.fetchData<UpdateTagRequest, TagSchema>({
      url: TagEndpoints.UPDATE_TAG(tagId),
      method: "PUT",
      data: request,
    });
    return buildTag(response.data);
  }
}

const buildTag = (tag: TagSchema): Tag =>
  new Tag(
    tag.id,
    tag.name,
    tag.description,
    new Date(tag.createdAt),
    new Date(tag.updatedAt),
  );
